/**
 * CompaniesList — el listado de compañías.
 *
 * QUÉ MOSTRABA MAL Y POR QUÉ (2026-08-04)
 * ---------------------------------------
 * Tres columnas leían campos que no existen, así que la pantalla mostraba ceros y
 * "Nunca" con toda seguridad:
 *
 *   · Métricas leía `metrics.ingresos_totales` y `metrics.total_elementos`. La
 *     vista `company_dashboard_metrics` no tiene ninguno de los dos → `$0` y
 *     `0 elementos` siempre. Ahora la plata sale de `amount_paid` de los cargos
 *     pagados, que es la misma cifra del cierre de YoolPay.
 *   · Suscripción leía `companias.subscription_status`, columna que no existe;
 *     `company_subscriptions` además está vacía. La columna se eliminó junto a su
 *     tarjeta y su filtro en vez de dejarla diciendo "Inactiva" para todas.
 *   · Última Sincronización leía `companias.last_sync_date`, que tampoco existe, y
 *     llamaba a `formatters.formatTime`, que no está exportado — la celda solo no
 *     reventaba porque nunca llegaba a renderizarse. Se eliminó, igual que el botón
 *     "Sincronizar YoolAdmin", que llamaba a `syncAllCompanies()`, un método que no
 *     existe en YoolAdminIntegrationService.
 *
 * Además: productos y planes contaban `producto.length` sin distinguir activo de
 * archivado, y el filtro "Estado" no filtraba nada (`matchesStatus` era siempre
 * true). Ese select ahora filtra por cobro, que es el dato que sí importa aquí.
 *
 * UNA CONSULTA, NO N
 * ------------------
 * Antes se llamaba `getCompanyMetrics(id)` una vez por compañía, con el cliente
 * anon del módulo en vez del cliente autenticado de Clerk. Ahora son tres consultas
 * fijas —compañías, métricas, recaudación— que no crecen con las filas.
 *
 * @see src/services/companiesService.js — de dónde sale cada cifra
 * @see src/pages/payments/yoolpay/YoolPayCierre.jsx — el cierre mensual, dueño de la
 *      verificación "lo recaudado tiene que ser lo girado"
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { Building2, FileCheck2, Plus, RefreshCw, Wallet } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { KpiCard } from '../../components/ui/KpiCard';
import {
  YoolTable,
  StatusPill,
  StackedCell,
  MonoText,
  RowActions,
  IconButton,
} from '../../components/ui/YoolTable';
import {
  getCompanies,
  getCompaniesMetrics,
  getRecaudacionPorCompania,
} from '../../services/companiesService';
import { useSupabaseClient } from '../../lib/supabase';
import formatters from '../../styles/tokens/formatters';

/** Compañía sin fila en la vista de métricas: ceros, no `null`. */
const METRICAS_CERO = {
  productos_total: 0, productos_activos: 0,
  planes_total: 0, planes_activos: 0,
  cotizaciones: 0, contrataciones: 0, certificados: 0,
};

/** Compañía sin cargos pagados. */
const RECAUDO_CERO = {
  recaudado_clp: 0, cuotas_pagadas: 0, cuotas_sin_monto: 0,
  propio_clp: 0, cuotas_propias: 0,
};

/**
 * Cómo cobra la compañía. Gris para pago propio y verde para Yool no es
 * decoración: es el mismo par que usa el cierre de YoolPay, donde la cuota
 * `delegated` de una compañía de pago propio va en gris porque no es un giro
 * pendiente — contarla como pendiente hace perseguir una transferencia que no
 * existe.
 *
 * @see src/components/ui/YoolTable.jsx — el mapa PILL que consume StatusPill
 */
const COBRO = {
  propio: { pill: 'inactivo', label: 'Pago propio' },
  yool:   { pill: 'activo',   label: 'Recauda Yool' },
};

/** Activos sobre total, con el total en gris para que el activo sea el que se lee. */
function Fraccion({ activos, total, etiqueta }) {
  return (
    <StackedCell
      primary={
        <span>
          {formatters.formatNumber(activos)}
          <span style={{ color: '#8D8D8D', fontWeight: 500 }}>
            {' / '}{formatters.formatNumber(total)}
          </span>
        </span>
      }
      secondary={etiqueta}
    />
  );
}

const CompaniesList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { session } = useSession();
  const supabase = useSupabaseClient();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      // En paralelo: ninguna depende del resultado de la otra.
      const [companias, metricas, recaudacion] = await Promise.all([
        getCompanies(supabase),
        getCompaniesMetrics(supabase),
        getRecaudacionPorCompania(supabase),
      ]);

      setRows(companias.map((c) => {
        const m = metricas.get(c.id) || METRICAS_CERO;
        const r = recaudacion.get(c.id) || RECAUDO_CERO;
        return {
          ...c,
          ...m,
          ...r,
          // `cobro` va como campo plano porque el filtro de YoolTable compara
          // `String(row[key])` contra el valor elegido: un derivado anidado no le sirve.
          cobro: c.pago_propio?.usa_propio ? 'propio' : 'yool',
          proveedor_cobro: c.pago_propio?.proveedor || null,
        };
      }));
    } catch (error) {
      console.error('Error al cargar compañías:', error);
      toast.error(`No se pudieron cargar las compañías: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    if (!session) return;
    cargar();
    // `session?.id` y no `session`: el objeto se reinstancia en cada render y
    // depender de él recarga la lista completa en cada pasada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, cargar]);

  const totales = useMemo(() => ({
    companias:    rows.length,
    pago_propio:  rows.filter((r) => r.cobro === 'propio').length,
    // `recaudado` es solo lo que pasó por Yool. `propio` va aparte, no sumado.
    recaudado:    rows.reduce((s, r) => s + r.recaudado_clp, 0),
    propio:       rows.reduce((s, r) => s + r.propio_clp, 0),
    sin_monto:    rows.reduce((s, r) => s + r.cuotas_sin_monto, 0),
    certificados: rows.reduce((s, r) => s + r.certificados, 0),
  }), [rows]);

  const columns = [
    {
      key: 'nombre_corto',
      label: 'Compañía',
      sortable: true,
      render: (r) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            {r.logo_url ? (
              <img
                src={r.logo_url}
                alt={r.nombre_corto}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-gray-500" />
              </div>
            )}
          </div>
          <StackedCell
            primary={r.nombre_corto}
            secondary={<MonoText>{formatters.formatRUT(r.rut)}</MonoText>}
          />
        </div>
      ),
    },
    {
      key: 'cobro',
      label: 'Cobro',
      sortable: true,
      render: (r) => {
        const c = COBRO[r.cobro];
        return (
          <div style={{ lineHeight: 1.5 }}>
            <StatusPill estado={c.pill} tone="ring">{c.label}</StatusPill>
            <div style={{ fontSize: 11, color: '#8D8D8D', marginTop: 3 }}>
              {r.cobro === 'propio'
                ? 'recauda en su cuenta'
                : r.proveedor_cobro || 'sin proveedor'}
            </div>
          </div>
        );
      },
    },
    {
      key: 'recaudado_clp',
      label: 'Recaudado',
      align: 'right',
      sortable: true,
      render: (r) => {
        // La plata de pago propio va aparte, nunca sumada: la cobró la compañía en
        // su cuenta y Yool no la recauda ni la gira. Un cero aquí con cuotas propias
        // no es un dato faltante — es la respuesta correcta.
        const propio = r.cuotas_propias > 0
          ? `${formatters.formatCLP(r.propio_clp)} lo cobró la compañía`
          : null;

        if (r.cuotas_pagadas === 0) {
          return (
            <StackedCell
              primary={<span style={{ color: '#8D8D8D' }}>—</span>}
              secondary={propio || (r.cobro === 'propio' ? 'cobra la compañía' : 'sin cobros')}
            />
          );
        }
        return (
          <StackedCell
            primary={formatters.formatCLP(r.recaudado_clp)}
            secondary={
              `${formatters.formatNumber(r.cuotas_pagadas)} cuotas`
              + (r.cuotas_sin_monto
                ? ` · ${formatters.formatNumber(r.cuotas_sin_monto)} sin monto`
                : '')
              + (propio ? ` · ${propio}` : '')
            }
          />
        );
      },
    },
    {
      key: 'cotizaciones',
      label: 'Embudo',
      sortable: true,
      render: (r) => (
        <StackedCell
          primary={`${formatters.formatNumber(r.cotizaciones)} cotizaciones`}
          secondary={
            `${formatters.formatNumber(r.contrataciones)} contratadas · `
            + `${formatters.formatNumber(r.certificados)} certificados`
          }
        />
      ),
    },
    {
      key: 'productos_activos',
      label: 'Productos',
      align: 'right',
      sortable: true,
      render: (r) => (
        <Fraccion activos={r.productos_activos} total={r.productos_total} etiqueta="activos" />
      ),
    },
    {
      key: 'planes_activos',
      label: 'Planes',
      align: 'right',
      sortable: true,
      render: (r) => (
        <Fraccion activos={r.planes_activos} total={r.planes_total} etiqueta="activos" />
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (r) => (
        <RowActions>
          <IconButton
            icon="refresh"
            title="Ver Dashboard"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/companies/${r.id}/dashboard`);
            }}
          />
          <IconButton
            icon="eye"
            title="Ver Detalles"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/companies/${r.id}`);
            }}
          />
          <IconButton
            icon="edit"
            title="Configuración"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/companies/${r.id}/settings`);
            }}
          />
        </RowActions>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Compañías</h1>
          <p className="text-muted-foreground">
            Quién cobra, cuánto se recaudó y qué tiene publicado cada compañía
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={cargar} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={() => navigate('/companies/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Nueva Compañía
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Compañías"
          value={formatters.formatNumber(totales.companias)}
          icon={<Building2 />}
          accent="blue"
          loading={loading}
        />
        <KpiCard
          label="Pago propio"
          value={formatters.formatNumber(totales.pago_propio)}
          icon={<Wallet />}
          accent="slate"
          context={`de ${formatters.formatNumber(totales.companias)} · el resto recauda Yool`}
          loading={loading}
        />
        <KpiCard
          label="Recaudado por Yool"
          value={formatters.formatCLP(totales.recaudado)}
          icon={<Wallet />}
          accent="teal"
          // Dos avisos que evitan leer mal la cifra: lo de pago propio no está
          // sumado aquí, y si hay cuotas pagadas sin `amount_paid` el total es
          // parcial. Un número redondo que no cuadra con el cierre es peor que
          // un número con nota al pie.
          context={[
            totales.propio ? `${formatters.formatCLP(totales.propio)} de pago propio, aparte` : null,
            totales.sin_monto ? `${formatters.formatNumber(totales.sin_monto)} cuotas sin monto` : null,
          ].filter(Boolean).join(' · ') || 'cuotas pagadas, en pesos'}
          loading={loading}
        />
        <KpiCard
          label="Certificados"
          value={formatters.formatNumber(totales.certificados)}
          icon={<FileCheck2 />}
          accent="violet"
          loading={loading}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Compañías</CardTitle>
        </CardHeader>
        <CardContent>
          <YoolTable
            columns={columns}
            rows={rows}
            rowKey="id"
            initialSort={{ key: 'recaudado_clp', dir: 'desc' }}
            search={{
              placeholder: 'Buscar por nombre, RUT o razón social…',
              keys: ['nombre_corto', 'rut', 'razon_social'],
            }}
            filters={[{
              key: 'cobro',
              label: 'Cobro',
              options: [
                { value: 'propio', label: COBRO.propio.label },
                { value: 'yool', label: COBRO.yool.label },
              ],
            }]}
            empty={loading
              ? { icon: 'refresh', title: 'Cargando compañías…', text: ' ' }
              : { icon: 'search', title: 'No hay compañías' }}
            variant="plain"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CompaniesList;
